import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardBody, Chip, Spinner } from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  getMediaOverviewStats,
  type MediaOverviewStats,
} from '../services/mediaManagementService'

const DocumentIcon = () => <span>📄</span>
const GifIcon = () => <span>🖼️</span>
const Mp4Icon = () => <span>🎬</span>
const WarningIcon = () => <span>⚠️</span>
const CheckIcon = () => <span>✅</span>

export const MediaOverviewStatsCard: React.FC = () => {
  const [stats, setStats] = useState<MediaOverviewStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toast = useToast()

  const loadStats = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await getMediaOverviewStats()
      setStats(result)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load stats'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Determine if there are any mismatches
  const hasIssues =
    stats &&
    (stats.totalGifDocuments !== stats.totalGifFiles ||
      stats.totalGifDocuments !== stats.totalMp4Files ||
      stats.totalGifFiles !== stats.totalMp4Files)

  const getStatusColor = () => {
    if (isLoading || !stats) return 'default'
    return hasIssues ? 'warning' : 'success'
  }

  const getStatusIcon = () => {
    if (isLoading) return <Spinner size="sm" />
    if (!stats) return <WarningIcon />
    return hasIssues ? <WarningIcon /> : <CheckIcon />
  }

  const getStatusText = () => {
    if (isLoading) return 'Loading...'
    if (error) return 'Error loading stats'
    if (!stats) return 'No data'
    return hasIssues ? 'Issues detected' : 'All counts match'
  }

  return (
    <Card>
      <CardBody className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Media Overview</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              Sanity check - these numbers should all be the same
            </p>
          </div>
          <Chip
            color={getStatusColor()}
            variant="flat"
            startContent={getStatusIcon()}
            size="sm"
          >
            {getStatusText()}
          </Chip>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">
              <WarningIcon />
            </div>
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={loadStats}
              className="mt-2 text-blue-600 hover:text-blue-800 underline"
            >
              Retry
            </button>
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* GIF Documents */}
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-2xl mb-2">
                <DocumentIcon />
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.totalGifDocuments.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                GIF Documents
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                (Firestore)
              </div>
            </div>

            {/* GIF Files */}
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-2xl mb-2">
                <GifIcon />
              </div>
              <div
                className={`text-2xl font-bold ${
                  stats.totalGifFiles !== stats.totalGifDocuments
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-slate-900 dark:text-slate-100'
                }`}
              >
                {stats.totalGifFiles.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                GIF Files
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                (Storage)
              </div>
            </div>

            {/* MP4 Files */}
            <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-2xl mb-2">
                <Mp4Icon />
              </div>
              <div
                className={`text-2xl font-bold ${
                  stats.totalMp4Files !== stats.totalGifDocuments
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-slate-900 dark:text-slate-100'
                }`}
              >
                {stats.totalMp4Files.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                MP4 Files
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                (Storage)
              </div>
            </div>
          </div>
        ) : null}

        {stats && hasIssues && (
          <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-start space-x-2">
              <div className="text-orange-600 dark:text-orange-400 mt-0.5">
                <WarningIcon />
              </div>
              <div>
                <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Count Mismatch Detected
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                  The numbers don&apos;t match, indicating missing files or
                  orphaned documents. Use the tabs below to investigate and fix
                  issues.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default MediaOverviewStatsCard
